import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, toZonedTime } from "date-fns-tz";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Converts a hex color (#RGB #RGBA #RRGGBB #RRGGBBAA) to RGB(A) [0-255, 0-255, 0-255, 0-1] */
export const hexToRGB = (color: string): [number, number, number, number] => {
  const match = color.match(/^#([\da-f]+)$/);

  if (!match) return [0, 0, 0, 1];
  let hex = match[1];
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] + "ff";
  } else if (hex.length === 4) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  } else if (hex.length === 6) {
    hex += "ff";
  } else if (hex.length !== 8) {
    return [0, 0, 0, 1];
  }
  const [r, g, b, a] = hex.match(/../g)!;
  return [
    parseInt(r, 16),
    parseInt(g, 16),
    parseInt(b, 16),
    parseInt(a, 16) / 255,
  ];
};

/** Gets the computed luminance of a hex color */
export const getBrightness = (color: string) => {
  const [r, g, b] = hexToRGB(color);
  return (r / 255) * 0.299 + (g / 255) * 0.587 + (b / 255) * 0.114;
};

/** Determine the text color based on the background color's brightness */
export const getTextColor = (color: string) => {
  return getBrightness(color) > 0.5 ? "black" : "white";
};

export const pickFields = <T extends object, K extends keyof T>(
  obj: T,
  fields: K[]
) => {
  const newObj = {} as T;
  Object.setPrototypeOf(newObj, Object.getPrototypeOf(obj));
  for (const key of fields) {
    if (key in obj) newObj[key] = obj[key];
  }
  return newObj as Pick<T, K>;
};

export const omitFields = <T extends object, K extends keyof T>(
  obj: T,
  fieldsToOmit: K[]
) => {
  const newObj = {} as T;
  Object.setPrototypeOf(newObj, Object.getPrototypeOf(obj));
  for (const key of Object.keys(obj) as K[]) {
    if (!fieldsToOmit.includes(key)) newObj[key] = obj[key];
  }
  return newObj as Omit<T, K>;
};

export function downloadFile(fileUrl: string | Blob, fileName: string = "") {
  const url = fileUrl instanceof Blob ? URL.createObjectURL(fileUrl) : fileUrl;

  // Create a temporary anchor element
  const anchorElement = document.createElement("a");
  anchorElement.href = url;
  anchorElement.target = "_blank";
  anchorElement.download = fileName;

  // Trigger a click event on the anchor element
  document.body.appendChild(anchorElement);
  anchorElement.click();

  // Cleanup
  document.body.removeChild(anchorElement);
  if (fileUrl instanceof Blob) URL.revokeObjectURL(url);
}

export function openInNewTab(url: string) {
  // Create a temporary anchor element
  const anchorElement = document.createElement("a");
  anchorElement.href = url;
  anchorElement.target = "_blank";

  // Trigger a click event on the anchor element
  document.body.appendChild(anchorElement);
  anchorElement.click();

  // Cleanup
  document.body.removeChild(anchorElement);
}

export const wholeNumber = (value: string | number, max: number = Infinity) =>
  Math.min(Math.max(Number(value) || 0, 1), max);

export function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function jsonStringifyWithUnicode(
  ...args: Parameters<JSON["stringify"]>
) {
  return JSON.stringify(...args).replace(
    /[\u0100-\uffff]/g,
    (u) => "\\u" + (u.charCodeAt(0) + 65536).toString(16).slice(1)
  );
}

export function arrayify<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function base64urlEncode(value: string) {
  return btoa(value).replace(/[+/]/g, (m) => ({ "+": "-", "/": "_" })[m]!);
}

export function base64urlDecode(value: string) {
  return atob(value.replace(/[-_]/g, (m) => ({ "-": "+", _: "/" })[m]!));
}

/** Join string pieces together with glue, but only if the pieces are not undefined */
export const concatIf = (...pieces: (string | undefined)[]) => {
  let joined = "";
  for (let i = 0; i < pieces.length; i++) {
    if (i % 2) {
      if (pieces[i - 1] && pieces[i]) joined += pieces[i];
    } else {
      if (pieces[i]) joined += pieces[i];
    }
  }
  return joined;
};

export function formatTZ(date: Date, formatStr: string, timeZone: string) {
  return format(toZonedTime(date, timeZone), formatStr, { timeZone });
}

export function uniqueArray<T>(
  array: T[],
  comparator: (a: T, b: T) => boolean
) {
  return array.filter(
    (element, index, self) =>
      index === self.findIndex((i) => comparator(i, element))
  );
}

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + " Bs";
  bytes /= 1024;
  if (bytes < 1024) return Math.round(bytes * 100) / 100 + " KBs";
  bytes /= 1024;
  if (bytes < 1024) return Math.round(bytes * 100) / 100 + " MBs";
  bytes /= 1024;
  if (bytes < 1024) return Math.round(bytes * 100) / 100 + " GBs";
  bytes /= 1024;
  return Math.round(bytes * 100) / 100 + " TBs";
};

export const fileToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const getURLHostname = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url.match(/^(?:https?:\/\/)?([^/]*)/)?.[1] || "<Empty URL>";
  }
};

export const fixInvalidURL = (url: string) => {
  if (!url) return null;

  try {
    return new URL(url).href;
  } catch {
    // noop
  }

  if (!/^https?:\/\//.test(url)) url = url.replace(/^\/*/g, "http://");

  try {
    return new URL(url).href;
  } catch {
    // noop
  }

  return null;
};
